import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Box,
  Container,
  Stack,
  Divider,
  Typography,
  Chip,
} from '@mui/material';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded';
import { useTheme } from '@mui/material/styles';
import logo from '../../assets/images/MC3.png';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about-us' },
  { label: 'Services', path: '/our-services' },
  { label: 'Expertise', path: '/expertize' },
  { label: 'Support', path: '/support' },
  { label: 'Careers', path: '/careers', badge: 'Hiring' },
];

const Navbar = () => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 24);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleDrawer = () => {
    setMobileOpen((prev) => !prev);
  };

  const DesktopLinks = () => (
    <Stack direction="row" alignItems="center" spacing={1.5} component="nav">
      {navLinks.map((item) => {
        const isActive = pathname === item.path || (item.path !== '/' && pathname.startsWith(item.path));
        return (
          <Button
            key={item.path}
            component={Link}
            to={item.path}
            color="inherit"
            sx={{
              color: isActive ? theme.palette.secondary.main : theme.palette.text.primary,
              fontWeight: 600,
              px: 1.5,
              '&:hover': {
                color: theme.palette.secondary.main,
              },
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <span>{item.label}</span>
              {item.badge && (
                <Chip
                  label={item.badge}
                  color="secondary"
                  size="small"
                  sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                />
              )}
            </Stack>
          </Button>
        );
      })}
      <Button
        component={Link}
        to="/contact-us"
        variant="contained"
        startIcon={<PhoneInTalkRoundedIcon />}
        sx={{
          ml: 1,
          background: 'linear-gradient(120deg, #f97316, #14b8a6)',
          color: '#fff',
          px: 3,
          '&:hover': {
            background: 'linear-gradient(120deg, #ea580c, #0d9488)',
          },
        }}
      >
        Book a Call
      </Button>
    </Stack>
  );

  const drawerContent = (
    <Box
      sx={{
        width: { xs: '100vw', sm: 360 },
        bgcolor: theme.palette.background.default,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
      role="presentation"
    >
      <Box>
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box component={Link} to="/" onClick={() => setMobileOpen(false)} sx={{ display: 'inline-flex' }}>
            <img src={logo} alt="MC3 Smart Tech logo" height={40} />
          </Box>
          <IconButton onClick={toggleDrawer} aria-label="Close navigation menu">
            <CloseRoundedIcon />
          </IconButton>
        </Box>
        <Divider />
        <List>
          {navLinks.map((item) => (
            <ListItemButton
              key={item.path}
              component={Link}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              selected={pathname === item.path}
            >
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body1" fontWeight={600}>
                      {item.label}
                    </Typography>
                    {item.badge && (
                      <Chip label={item.badge} size="small" color="secondary" sx={{ height: 18 }} />
                    )}
                  </Stack>
                }
              />
            </ListItemButton>
          ))}
        </List>
      </Box>
      <Box sx={{ p: 3 }}>
        <Button
          component={Link}
          to="/contact-us"
          fullWidth
          variant="contained"
          startIcon={<PhoneInTalkRoundedIcon />}
          onClick={() => setMobileOpen(false)}
          sx={{
            background: 'linear-gradient(120deg, #f97316, #14b8a6)',
            color: '#fff',
          }}
        >
          Book a Strategy Session
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
          Or call us at +1 518 394 4014
        </Typography>
      </Box>
    </Box>
  );

  return (
    <AppBar
      position="sticky"
      elevation={isScrolled ? 6 : 0}
      sx={{
        backgroundColor: isScrolled ? 'rgba(248,250,252,0.9)' : 'transparent',
        color: theme.palette.text.primary,
        backdropFilter: isScrolled ? 'saturate(180%) blur(20px)' : 'none',
        transition: 'all 0.3s ease',
        borderBottom: isScrolled ? '1px solid rgba(15,23,42,0.08)' : '1px solid transparent',
      }}
    >
      <Container>
        <Toolbar disableGutters sx={{ py: 1.5, display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Link to="/" aria-label="MC3 Smart Tech home">
              <img src={logo} alt="MC3 Smart Tech" style={{ height: 54 }} />
            </Link>
            <Typography
              variant="subtitle2"
              sx={{ ml: 2, letterSpacing: '0.08em', display: { xs: 'none', md: 'inline-flex' }, color: 'text.secondary' }}
            >
              Build · Secure · Scale
            </Typography>
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
            <DesktopLinks />
          </Box>
          <IconButton
            color="inherit"
            edge="end"
            onClick={toggleDrawer}
            sx={{ display: { xs: 'inline-flex', md: 'none' }, color: theme.palette.text.primary }}
            aria-label="Open navigation menu"
          >
            <MenuRoundedIcon />
          </IconButton>
        </Toolbar>
      </Container>
      <Drawer anchor="right" open={mobileOpen} onClose={toggleDrawer}>
        {drawerContent}
      </Drawer>
    </AppBar>
  );
};

export default Navbar;
